import MapPlan from "@/helpers/maps/MapPlan.js";

const latestStartId = localStorage.getItem(`latestStartId`);
const latestEndId = localStorage.getItem(`latestEndId`);
const startId = localStorage.getItem(`mapStartId`);
const endId = localStorage.getItem(`mapEndId`);
const latestLocationIdsStorage = localStorage.getItem(`latestLocationIds`);
const latestLocationIds = latestLocationIdsStorage ? JSON.parse(latestLocationIdsStorage) : [];
const LATEST_LOCATION_LIMIT = 5;

export default {
    namespaced: true,
    state: () => ({
        map: null,
        floor: null,
        building: null,
        debug: {
            enabled: false,
            pano: false,
            map: false,
            links: false,
            // debug the current path
            path: false,
            // debug the pathfinding instance and cost
            pathfinder: false,
            svg: false,
            menuVisible: false,
            freeNavigation: false,
            emulateGPS: false,
            noisyGPS: false,
            pedometerWithoutGps: false,
            disableOrientation: false,
            showArPanoGridInLive: false,
            orientation: {
                enabled: false,
                alpha: 0,
                targetAlpha: null,
                angleDeadZone: 0.18,
                isHolding: false,
                smoothingRaf: null,
                maxTurnSpeedDegPerSec: 540
            }
        },
        navSettings: {
            static: true,
            compass: false,
            blueDotNavigation: false,
            image360: false,
            pedometer: false,
            layout: `map`,
            // preferences are used by the pathfinder
            preferences: {
                accessible: true
            }
        },
        width: 1920,
        height: 1080,
        startId,
        endId,
        latestStartId,
        latestEndId,
        latestLocationIds,
        latestQrCodeItems: [],
        isCurrentLocationOnSite: null
    }),
    actions: {},
    mutations: {
        setMap(state, map) {
            state.map = new MapPlan(map);

            // Kiosks: request every floor image right away so the service worker caches them all,
            // making floor switching work offline even for floors never browsed while online.
            // Same detection as App.vue, which may not have run yet when the first map is set.
            const isKioskDevice = window.isKioskDevice ||
                new URLSearchParams(window.location.search).get(`display`) === `kiosk` ||
                localStorage.getItem(`kiosk`) === `yes`;
            if (isKioskDevice && state.map) {
                for (const floor of state.map.floors || []) {
                    if (floor.imagePath && typeof floor.imagePath === `string`) {
                        const image = new Image();
                        image.src = floor.imagePath;
                    }
                }

                // The exterior image is only rendered by floors displaying it, prefetch it too
                const exteriorImageUrl = state.map.getExteriorImageUrl();
                if (exteriorImageUrl) {
                    const image = new Image();
                    image.src = exteriorImageUrl;
                }
            }
        },
        setFloor(state, floor) {
            state.floor = floor;
        },
        setBuilding(state, building) {
            state.building = building;
        },
        setStartId(state, id) {
            state.startId = id || null;
            if (state.startId) {
                localStorage.setItem(`mapStartId`, state.startId);
            } else {
                localStorage.removeItem(`mapStartId`);
            }
        },
        setEndId(state, id) {
            state.endId = id || null;
            if (state.endId) {
                localStorage.setItem(`mapEndId`, state.endId);
            } else {
                localStorage.removeItem(`mapEndId`);
            }
        },
        setLatestStartId(state, id) {
            state.latestStartId = id || null;
            if (state.latestStartId) {
                localStorage.setItem(`latestStartId`, state.latestStartId);
                state.latestLocationIds = state.latestLocationIds.filter(value => value !== state.latestStartId);
                state.latestLocationIds.unshift(state.latestStartId);
                state.latestLocationIds = state.latestLocationIds.slice(0, LATEST_LOCATION_LIMIT);
                localStorage.setItem(`latestLocationIds`, JSON.stringify(state.latestLocationIds));
            } else {
                localStorage.removeItem(`latestStartId`);
            }
        },
        setLatestEndId(state, id) {
            state.latestEndId = id || null;
            if (state.latestEndId) {
                localStorage.setItem(`latestEndId`, state.latestEndId);
                state.latestLocationIds = state.latestLocationIds.filter(value => value !== state.latestEndId);
                state.latestLocationIds.unshift(state.latestEndId);
                state.latestLocationIds = state.latestLocationIds.slice(0, LATEST_LOCATION_LIMIT);
                localStorage.setItem(`latestLocationIds`, JSON.stringify(state.latestLocationIds));
            } else {
                localStorage.removeItem(`latestEndId`);
            }
        },
        addLatestQrCodeItem(state, mapItem) {
            for (let index = state.latestQrCodeItems.length - 1; index >= 0; index -= 1) {
                if (state.latestQrCodeItems[index]?.id === mapItem.id) {
                    state.latestQrCodeItems.splice(index, 1);
                }
            }
            
            state.latestQrCodeItems = state.latestQrCodeItems.filter(item => item != null);

            if (!mapItem.qrCode) {
                return;
            }

            state.latestQrCodeItems.push(mapItem);
            if (state.latestQrCodeItems.length > 5) {
                state.latestQrCodeItems.shift();
            }
            if (state.latestQrCodeItems.length > 0) {
                localStorage.setItem(`latestQrCodeItems`, JSON.stringify(state.latestQrCodeItems.map(value => value.qrCode)));
            } else {
                localStorage.removeItem(`latestQrCodeItems`);
            }

        },
        clearEndpoints(state) {
            state.startId = null;
            state.endId = null;
            localStorage.removeItem(`mapStartId`);
            localStorage.removeItem(`mapEndId`);
        },
        clearAllEndpoints(state) {
            state.startId = null;
            state.endId = null;
            state.latestStartId = null;
            state.latestEndId = null;
            state.latestLocationIds = [];
            state.latestQrCodeItems = [];
            localStorage.removeItem(`mapStartId`);
            localStorage.removeItem(`mapEndId`);
            localStorage.removeItem(`latestStartId`);
            localStorage.removeItem(`latestEndId`);
            localStorage.removeItem(`latestLocationIds`);
        },
        setIsCurrentLocationOnSite(state, value) {
            if (value === null) {
                state.isCurrentLocationOnSite = null;
                return;
            }
            state.isCurrentLocationOnSite = !!value;
        }
    }
}
